import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PRAYER_ACCENT, testimonyDraft } from "@/lib/prayers";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from "@/components/ui/dialog";

const CATS = [
  { key: "healing", tKey: "testimony.cat_healing", fb: "Guérison" },
  { key: "provision", tKey: "testimony.cat_provision", fb: "Provision" },
  { key: "salvation", tKey: "testimony.cat_salvation", fb: "Salut" },
  { key: "family", tKey: "testimony.cat_family", fb: "Famille" },
  { key: "other", tKey: "testimony.cat_other", fb: "Autre" },
];

// Answered prayer (owner) → one tap « Publier comme témoignage » (editable text + category) → post in the feed + Témoignages.
export const PrayerToTestimony = ({ p, onPublished }) => {
  const { t, config } = useApp();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("other");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  if (config?.settings?.features?.testimonies === false || !p.is_answered || !p.is_mine) return null;

  if (p.testimony_post_id) {
    return (
      <Link to={`/post/${p.testimony_post_id}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: PRAYER_ACCENT }} data-testid={`prayer-testimony-link-${p.id}`}>
        <Sparkles size={13} /> {t("prayers.testimony_view", "Voir le témoignage")} <ExternalLink size={11} />
      </Link>
    );
  }

  const start = () => { setContent(testimonyDraft(p, t)); setCategory("other"); setOpen(true); };
  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/prayers/${p.id}/testimony`, { category, content });
      toast.success(t("prayers.testimony_published", "Témoignage publié ✨"));
      setOpen(false);
      onPublished?.(p.id, data.testimony_post_id, data.post);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setBusy(false);
  };

  return (
    <>
      <Button size="sm" className="rounded-full" style={{ background: PRAYER_ACCENT, color: "#fff" }} onClick={start} data-testid={`prayer-to-testimony-${p.id}`}>
        <Sparkles size={14} className="mr-1" /> {t("prayers.publish_testimony", "Publier comme témoignage")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent variant="sheet" data-testid={`prayer-testimony-dialog-${p.id}`}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{t("prayers.testimony_dialog_title", "Partager ce témoignage")}</DialogTitle>
            <DialogDescription>{t("prayers.testimony_dialog_desc", "Ta prière exaucée sera publiée dans Témoignages et dans le fil, pour encourager la communauté.")}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div>
              <Label>{t("testimony.category", "Catégorie")}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {CATS.map((c) => (
                  <button key={c.key} type="button" onClick={() => setCategory(c.key)} className={`chip ${category === c.key ? "!bg-[#2D5A4C] !text-white !border-transparent" : ""}`} data-testid={`prayer-testimony-cat-${c.key}-${p.id}`}>
                    {t(c.tKey, c.fb)}
                  </button>
                ))}
              </div>
            </div>
            <Textarea rows={7} value={content} maxLength={5000} onChange={(e) => setContent(e.target.value)} data-testid={`prayer-testimony-content-${p.id}`} />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>{t("common.cancel", "Annuler")}</Button>
            <Button style={{ background: PRAYER_ACCENT, color: "#fff" }} onClick={publish} disabled={busy || !content.trim()} data-testid={`prayer-testimony-publish-${p.id}`}>
              {busy ? "…" : t("testimony.share", "Partager")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
